import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	FormType,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { MediaAttachmentFormValues, StudentMedicalEventFormValues } from "../../FormUtilities";
import { EmployeeSelectField } from "../Employee";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import { UserSelectField } from "../User";

/**
 * Generic props for fields of the StudentMedicalEvent model.
 */
type FieldProps<K extends keyof StudentMedicalEventFormValues.Base = keyof StudentMedicalEventFormValues.Base> =
	BaseFieldProps<Pick<StudentMedicalEventFormValues.Base, K>>;

/**
 * Generic props for fields of the StudentMedicalEvent model that only appear in the create form.
 */
type CreateFieldProps<
	K extends keyof StudentMedicalEventFormValues.Create = keyof StudentMedicalEventFormValues.Create,
> = BaseFieldProps<Pick<StudentMedicalEventFormValues.Create, K>>;

/**
 * Generic props for fields of the StudentMedicalEvent model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof StudentMedicalEventFormValues.Detail = keyof StudentMedicalEventFormValues.Detail,
> = BaseFieldProps<Pick<StudentMedicalEventFormValues.Detail, K>>;

/**
 * Field component for the `checkInTime` field of the StudentMedicalEvent model.
 */
export function CheckInTime({ formState, ...props }: FieldProps<"checkInTime">) {
	return (
		<DateTimeField
			formState={formState}
			name="checkInTime"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `checkOutTime` field of the StudentMedicalEvent model.
 */
export function CheckOutTime({ formState, ...props }: FieldProps<"checkOutTime">) {
	return <DateTimeField formState={formState} name="checkOutTime" optional precision="minute" {...props} />;
}

/**
 * Field component for the `description` field of the StudentMedicalEvent model.
 */
export function Description({
	formState,
	label = "Description of medical incident or issue",
	...props
}: FieldProps<"description">) {
	return (
		<TextField formState={formState} name="description" label={label} multiLine Input={TwoRowTextInput} {...props} />
	);
}

/**
 * Field component for the `treatment` field of the StudentMedicalEvent model.
 */
export function Treatment({
	formState,
	label = "Treatment provided to the student",
	...props
}: FieldProps<"treatment">) {
	return (
		<TextField formState={formState} name="treatment" label={label} multiLine Input={TwoRowTextInput} {...props} />
	);
}

/**
 * Field component for the `outcome` field of the StudentMedicalEvent model.
 */
export function Outcome({ formState, label = "Outcome of treatment", ...props }: FieldProps<"outcome">) {
	return <TextField formState={formState} name="outcome" label={label} multiLine Input={TwoRowTextInput} {...props} />;
}

/**
 * Field component for the `notes` field of the StudentMedicalEvent model.
 */
export function Notes({ formState, ...props }: FieldProps<"notes">) {
	return <TextField formState={formState} name="notes" multiLine Input={TwoRowTextInput} optional {...props} />;
}

function TwoRowTextInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={2} {...props} />;
}

export namespace AttendingEmployees {
	export type SelectProps = FieldProps<"attendingEmployeeIDs"> &
		Omit<EmployeeSelectField.MultiSelectProps<StudentMedicalEventFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `attendingEmployees` edge of the StudentMedicalEvent model.
	 */
	export function Select({ formState, label = "Staff member(s) who provided treatment", ...props }: SelectProps) {
		return (
			<EmployeeSelectField.MultiSelect formState={formState} name="attendingEmployeeIDs" label={label} {...props} />
		);
	}
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<StudentMedicalEventFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the StudentMedicalEvent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace MediaAttachments {
	/**
	 * SubForm field component for the `mediaAttachments` edge of the StudentMedicalEvent model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"mediaAttachments">) {
		return (
			<MediaAttachmentCustomForm.SubForm
				formState={formState}
				name="mediaAttachments"
				blankItem={MediaAttachmentFormValues.blankItemInStudentMedicalEvent}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</MediaAttachmentCustomForm.SubForm>
		);
	}

	SubForm.SubForm = function (props: SubFormRenderProps<MediaAttachmentFormValues.CreateInStudentMedicalEvent>) {
		return (
			<MediaAttachmentCustomForm.SubForm.SubForm {...props}>
				<MediaAttachmentCustomForm.CreateFieldsInStudentMedicalEvent formState={props.formState} />
			</MediaAttachmentCustomForm.SubForm.SubForm>
		);
	};

	SubForm.AddButton = function (
		props: AddSubFormButtonRenderProps<
			Pick<StudentMedicalEventFormValues.Create, "mediaAttachments">,
			MediaAttachmentFormValues.CreateInStudentMedicalEvent
		>,
	) {
		return <MediaAttachmentCustomForm.SubForm.AddButton {...props} />;
	};
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<StudentSelectField.SelectProps<StudentMedicalEventFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `student` edge of the StudentMedicalEvent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" {...props} />;
	}
}
